import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock } from "lucide-react";

const endpoints = [
  {
    group: "Autenticazione portale (Clerk)",
    items: [
      { method: "GET", path: "/api/clerk/config", auth: "pubblica", desc: "Config dinamica Clerk per il frontend." },
      { method: "GET", path: "/api/me", auth: "Bearer utente", desc: "Ritorna il profilo utente corrente, ruolo e stato di approvazione." },
    ],
  },
  {
    group: "Area Rivenditore (Bearer utente)",
    items: [
      { method: "POST", path: "/api/reseller/profile", auth: "reseller", desc: "Crea/aggiorna l'anagrafica rivenditore." },
      { method: "POST", path: "/api/reseller/documents", auth: "reseller", desc: "Upload documento (identita, codice_fiscale, visura, agreement_signed, integrative_signed)." },
      { method: "POST", path: "/api/reseller/agreement/generate", auth: "reseller", desc: "Genera il PDF dell'accordo GoWeb-Rivenditore." },
      { method: "GET", path: "/api/reseller/products", auth: "reseller", desc: "Elenco prodotti assegnati." },
      { method: "GET /POST", path: "/api/reseller/customers", auth: "reseller", desc: "Lista o crea un cliente (genera il modulo d'ordine PDF)." },
      { method: "POST", path: "/api/reseller/customers/{id}/signed-order", auth: "reseller", desc: "Upload del modulo d'ordine firmato dal cliente." },
      { method: "GET", path: "/api/reseller/commissions", auth: "reseller", desc: "Provvigioni maturate e liquidate." },
      { method: "GET", path: "/api/reseller/plan", auth: "reseller", desc: "Piano provvigionale corrente e storico." },
      { method: "GET", path: "/api/reseller/dashboard", auth: "reseller", desc: "KPI del rivenditore (clienti, scadenze, provvigioni del mese)." },
    ],
  },
  {
    group: "Area Super Admin (Bearer utente, ruolo admin)",
    items: [
      { method: "GET", path: "/api/admin/dashboard", auth: "admin", desc: "Dashboard aggregata globale." },
      { method: "GET /POST /PUT /PATCH", path: "/api/admin/products", auth: "admin", desc: "CRUD prodotti + rotazione API key + toggle attivo." },
      { method: "GET /PATCH /PUT", path: "/api/admin/resellers", auth: "admin", desc: "Gestione rivenditori: blocco, assegnazione prodotti." },
      { method: "GET /POST", path: "/api/admin/commission-plans", auth: "admin", desc: "Piani provvigionali + storico (cambio piano genera modulo integrativo PDF)." },
      { method: "GET /POST", path: "/api/admin/approvals", auth: "admin", desc: "Coda approvazione documenti e moduli d'ordine." },
      { method: "GET /POST", path: "/api/admin/commissions", auth: "admin", desc: "Elenco provvigioni, liquidazione." },
      { method: "GET /PUT /POST", path: "/api/admin/smtp", auth: "admin", desc: "Configurazione SMTP del portale e invio email di test." },
    ],
  },
  {
    group: "API bidirezionali per prodotti GoWeb (X-Product-Api-Key)",
    items: [
      { method: "GET", path: "/api/external/ping", auth: "product-key", desc: "Verifica la chiave API e identifica il prodotto chiamante." },
      { method: "POST", path: "/api/external/renewals", auth: "product-key", desc: "Notifica rinnovo cliente. Il portale aggiorna le provvigioni secondo il piano corrente del rivenditore identificato tramite codice." },
      { method: "POST", path: "/api/external/plan-change", auth: "product-key", desc: "Notifica cambio piano/moduli di un cliente." },
      { method: "POST", path: "/api/external/deactivation", auth: "product-key", desc: "Segnala disattivazione di un cliente." },
    ],
  },
];

const sampleRenewal = `curl -X POST https://reseller.goweb.srl/api/external/renewals \\
  -H "Content-Type: application/json" \\
  -H "X-Product-Api-Key: gw_••••••••••••••••••••••••••••••••" \\
  -d '{
    "external_account_id": "BH-20250001",
    "reseller_code": "GW-DEMO-01",
    "license_amount": 499.00,
    "new_license_end": "2027-02-25T00:00:00Z"
  }'`;

const sampleActivation = `POST {api_base_url}/accounts      (chiamata dal portale verso l'app)
X-Portal-Api-Key: gw_•••• (chiave del portale per questo prodotto, in Admin > Chiavi API)
Authorization: Bearer <API key dell'app>   (impostata in Admin > Prodotti > Modifica)
X-Api-Key: <API key dell'app>
Content-Type: application/json

{
  "portal_customer_id": "9d279b20-...",
  "reseller_code": "GW-DEMO-01",
  "customer": { "nome": "Giulia", "cognome": "Bianchi", "email": "...", "telefono": "...",
                "dati_fiscali": { "ragione_sociale": "...", "piva": "...", "codice_fiscale": "...", "sdi_pec": "...", "sede": "..." } },
  "modules": [ { "id": "...", "name": "Prenotazioni Online" } ],
  "license_price": 590.00,
  "trial_start": "2026-10-25T10:00:00+00:00",
  "trial_end": "2026-11-24T10:00:00+00:00",
  "license_end": "2027-10-25T10:00:00+00:00"
}

Risposta attesa (2xx):  { "external_account_id": "BH-20260001" }
Timeout 15 s. In caso di errore il cliente viene attivato comunque nel portale
e l'esito resta nel registro attivazioni (Admin > Chiavi API).

---
GET {api_base_url}/portal/ping      (pulsante "Verifica collegamento" in Admin > Prodotti)
Stessi header (Authorization: Bearer <API key dell'app>, X-Api-Key, X-Portal-Api-Key)

Risposte attese:
  2xx + JSON (es. { "ok": true })  -> collegamento riuscito
  401/403                          -> chiave non valida
  404                              -> endpoint /portal/ping mancante nell'app
Per le app Emergent l'URL base include /api (es. https://booking-hub-1047.emergent.host/api).`;

export default function ApiDocs() {
  return (
    <div className="min-h-screen bg-[#0F141A] text-slate-100">
      <header className="sticky top-0 z-10 border-b border-slate-800 backdrop-blur bg-[#0F141A]/90">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="apidocs-back" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
              <ArrowLeft size={14} /> Indietro
            </Link>
            <div className="w-px h-5 bg-slate-800" />
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#D95D39]" />
              <div className="font-semibold tracking-tight">GoWeb Reseller API</div>
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">v1.0</span>
            </div>
          </div>
          <div className="text-xs text-slate-400 font-mono">reseller.goweb.srl/api</div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold tracking-tight">Documentazione API</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Il portale espone un set di API REST per l'autenticazione utenti, la gestione reseller/cliente/prodotto e un canale bidirezionale per i prodotti GoWeb (Booking Hub, Cantiere 360 Cloud ed estensioni future).
        </p>

        <div className="mt-8 grid lg:grid-cols-3 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
              <Lock size={12} /> Auth Utenti
            </div>
            <div className="mt-2 font-semibold">Bearer token</div>
            <p className="text-sm text-slate-400 mt-1">Clerk emette un JWT per super admin e rivenditori. Da inviare in <code className="font-mono text-[11px] bg-slate-800 px-1 py-0.5 rounded">Authorization: Bearer &lt;token&gt;</code>.</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
              <Lock size={12} /> Auth Prodotti
            </div>
            <div className="mt-2 font-semibold">API Key per prodotto</div>
            <p className="text-sm text-slate-400 mt-1">Ogni prodotto GoWeb riceve una chiave univoca. Da inviare in <code className="font-mono text-[11px] bg-slate-800 px-1 py-0.5 rounded">X-Product-Api-Key</code>. Rotazione dal pannello admin.</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500">Modularità</div>
            <div className="mt-2 font-semibold">Nuovi prodotti plug-in</div>
            <p className="text-sm text-slate-400 mt-1">Aggiungere un nuovo prodotto al catalogo è sufficiente: il portale lo rende rivendibile e disponibile sulle API esterne senza modifiche al core.</p>
          </div>
        </div>

        {endpoints.map((g) => (
          <div key={g.group} className="mt-10">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">{g.group}</h2>
            <div className="mt-3 rounded-xl border border-slate-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2 w-24">Metodo</th>
                    <th className="text-left px-4 py-2">Endpoint</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell w-28">Auth</th>
                    <th className="text-left px-4 py-2">Descrizione</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((e, i) => (
                    <tr key={i} className="border-t border-slate-800 hover:bg-slate-900/50">
                      <td className="px-4 py-2.5"><span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-[#206342]/30 text-[#5EE09E] border border-[#206342]/50">{e.method}</span></td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-slate-200">{e.path}</td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-xs text-slate-400">{e.auth}</td>
                      <td className="px-4 py-2.5 text-slate-300">{e.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Esempio: notifica rinnovo</h2>
          <pre className="mt-3 bg-slate-900 border border-slate-800 rounded-xl p-5 text-xs font-mono text-slate-200 overflow-auto">{sampleRenewal}</pre>
        </div>

        <div className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Attivazione cliente: chiamata dal portale verso l'app</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-3xl">Quando il Super Admin approva il modulo d'ordine firmato, il portale chiama l'app del prodotto per creare l'account. Ogni app deve esporre questo endpoint.</p>
          <pre className="mt-3 bg-slate-900 border border-slate-800 rounded-xl p-5 text-xs font-mono text-slate-200 overflow-auto" data-testid="apidocs-activation">{sampleActivation}</pre>
        </div>

        <div className="mt-10 text-xs text-slate-500 border-t border-slate-800 pt-6">
          Specifica OpenAPI generata automaticamente disponibile su <code className="font-mono bg-slate-800 px-1.5 py-0.5 rounded">/openapi.json</code> e UI interattiva su <code className="font-mono bg-slate-800 px-1.5 py-0.5 rounded">/docs</code> del backend.
        </div>
      </section>
    </div>
  );
}
